import { Platform } from 'react-native';
import type { TextStyle } from 'react-native';
import { useAppStore } from '@restorant/core';
import type { TextSize, ThemeMode } from '@restorant/core';

/**
 * Both palettes carry the same token names, so components never branch on the
 * theme — they read `useColors()` and the right values arrive. Every
 * text/background pair below meets WCAG AA (4.5:1).
 */
export interface Palette {
  bg: string;
  surface: string;
  surface2: string;
  line: string;
  gold: string;
  goldDim: string;
  spice: string;
  spiceDim: string;
  text: string;
  textDim: string;
  textFaint: string;
  good: string;
  /** Text/icon colour that sits on top of a gold fill. */
  onGold: string;
  sidebarTop: string;
  /** Scrim behind modals. */
  scrim: string;
}

export const lightPalette: Palette = {
  bg: '#FBF7F0',
  surface: '#FFFFFF',
  surface2: '#F4EEE2',
  line: '#E2D8C6',
  gold: '#8B5F0C',
  goldDim: '#B58A3A',
  spice: '#AF3F1B',
  spiceDim: '#8C3B22',
  text: '#231E16',
  textDim: '#574E41',
  textFaint: '#6F6553',
  good: '#3B7429',
  onGold: '#FFFDF6',
  sidebarTop: '#F5EFE3',
  scrim: 'rgba(35,30,22,0.45)',
};

export const darkPalette: Palette = {
  bg: '#16140F',
  surface: '#211D17',
  surface2: '#2A2419',
  line: '#3A3225',
  gold: '#D9A94A',
  goldDim: '#8A6F35',
  spice: '#C1502E',
  spiceDim: '#8C3B22',
  text: '#F2E9D8',
  textDim: '#B4AA95',
  textFaint: '#948A78',
  good: '#6E9B5E',
  onGold: '#1A1608',
  sidebarTop: '#1B1811',
  scrim: 'rgba(0,0,0,0.66)',
};

export const PALETTES: Record<ThemeMode, Palette> = {
  light: lightPalette,
  dark: darkPalette,
};

export function getPalette(mode: ThemeMode): Palette {
  return PALETTES[mode] ?? lightPalette;
}

/** The palette for the active theme. Re-renders the caller when it changes. */
export function useColors(): Palette {
  return getPalette(useAppStore((s) => s.theme));
}

export function useThemeMode(): ThemeMode {
  return useAppStore((s) => s.theme);
}

/**
 * Reading size. 'md' is the default and is deliberately larger than the
 * original design, which was hard to read on a phone held at arm's length
 * across a counter. 'sm' reproduces the old sizing for anyone who prefers it.
 */
export const TEXT_SCALES: { id: TextSize; labelKey: string; value: number }[] = [
  { id: 'sm', labelKey: 'size_sm', value: 1.0 },
  { id: 'md', labelKey: 'size_md', value: 1.15 },
  { id: 'lg', labelKey: 'size_lg', value: 1.32 },
  { id: 'xl', labelKey: 'size_xl', value: 1.5 },
];

/** Nothing may render below this, however far the scale is turned down. */
export const MIN_FONT_SIZE = 11;

export function textScaleFor(size: TextSize): number {
  return TEXT_SCALES.find((s) => s.id === size)?.value ?? 1.15;
}

/** The active text multiplier. */
export function useTextScale(): number {
  return textScaleFor(useAppStore((s) => s.textSize));
}

/**
 * Icons grow with the text but only half as fast: they sit inside fixed boxes,
 * and matching the text step for step makes the chrome look inflated.
 */
export function useIconScale(): number {
  const scale = useTextScale();
  return 1 + (scale - 1) * 0.5;
}

/** Applies the scale to a font size, respecting the floor. */
export function scaleFont(size: number, scale: number): number {
  return Math.max(MIN_FONT_SIZE, Math.round(size * scale * 10) / 10);
}

export const radius = { sm: 7, md: 10, lg: 16, pill: 999 } as const;

export const space = { xs: 4, sm: 6, md: 10, lg: 14, xl: 18, xxl: 26 } as const;

/** The breakpoint the original CSS used to swap the sidebar for a bottom bar. */
export const MOBILE_BREAKPOINT = 820;
/** Below this, the order screen stacks the cart under the menu. */
export const ORDER_STACK_BREAKPOINT = 980;

export type FontWeight = 400 | 500 | 600 | 700 | 800;

// On native these must match the names expo-font registers (see apps/mobile/App.tsx).
// On web the families come from the Google Fonts <link> in apps/web/index.html.
const NATIVE_SANS: Record<FontWeight, string> = {
  400: 'Manrope_400Regular',
  500: 'Manrope_500Medium',
  600: 'Manrope_600SemiBold',
  700: 'Manrope_700Bold',
  800: 'Manrope_800ExtraBold',
};

const NATIVE_SERIF: Record<FontWeight, string> = {
  400: 'Fraunces_500Medium',
  500: 'Fraunces_500Medium',
  600: 'Fraunces_600SemiBold',
  700: 'Fraunces_700Bold',
  800: 'Fraunces_700Bold',
};

/**
 * Native needs one concrete font file per weight; web gets a family plus a
 * numeric weight. `fontsReady` is false until expo-font finishes, so the first
 * frames on native fall back to the system face instead of rendering blank.
 */
export function fontStyle(
  weight: FontWeight = 400,
  serif = false,
  fontsReady = true,
): TextStyle {
  if (Platform.OS === 'web') {
    return {
      fontFamily: serif
        ? "'Fraunces', Georgia, 'Times New Roman', serif"
        : "'Manrope', system-ui, -apple-system, 'Segoe UI', sans-serif",
      fontWeight: String(weight) as TextStyle['fontWeight'],
    };
  }
  if (!fontsReady) {
    return {
      fontWeight: String(weight) as TextStyle['fontWeight'],
      ...(serif ? { fontFamily: Platform.OS === 'ios' ? 'Georgia' : 'serif' } : null),
    };
  }
  return { fontFamily: serif ? NATIVE_SERIF[weight] : NATIVE_SANS[weight] };
}

/** Native font names, exported so the mobile entry can load exactly these. */
export const NATIVE_FONT_NAMES = { sans: NATIVE_SANS, serif: NATIVE_SERIF };
